import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'
import { disableAggOptions } from '../utils/aggregationUtils'
import { getNowcastingProps, arrangeListByIndex } from '../utils/tools'
import { useProject } from './project'
import useFinancial from '../hooks/useFinancial'
import useDatasetsList from '../hooks/useDatasetsList'
import axios from 'axios'

const SectionContext = createContext()

export const SectionProvider = ({ sectionId, dragHandleProps, children }) => {
    const { ticker, colapseAllSections, deleteSection, duplicateSection, isLoading, updateAllSectionsDataset,ColumnGranularityOptions } = useProject()

    const { suggested, getFinancialNameByValue } = useFinancial(ticker)
    const [isExpand, setIsExpand] = useState(true)
    const [sectionDataset, setSectionDataset] = useState([])
    const [sectionFinancialInfo, setSectionFinancialInfo] = useState('')
    const [tab, setTab] = useState(0)
    const [analyze, setAnalyze] = useState(0)
    const [sectionName, setSectionName] = useState('')

    const dataset = useMemo(() => sectionDataset.map((item, index) => ({...item, index})),[sectionDataset])

    // currnt header
    const [selectedDataset, setSelectedDataset] = useState([])
    const [selectedFinancialInfo, setSelectedFinancialInfo] = useState('')
    const { updateDataset } = useDatasetsList(selectedDataset)

    //Nowcasting 
    const [sectionNowcastingFinancialInfo, setSectionNowcastingFinancialInfo] = useState('')
    const [sectionNowcastingDetails, setSectionNowcastingDetails] = useState(null)

    const getNowcastingModelInfo = (sectionId) =>{
        axios({
            url:"/api/getModelInfo",
            method:"POST",
            data:{section_id:sectionId}
        }).then(res=>{
            setSectionNowcastingDetails(pre=>{return {...pre, ...res.data}})
        })
    }

    // labels
    const datasetLabels = useMemo(() => {
    const financial = {
            columnName: getFinancialNameByValue(sectionFinancialInfo), 
            columnValue: sectionFinancialInfo, 
            datasetName: 'Financial Data', 
            datasetValue: 'financial_data'
        }
        return [...sectionDataset, {...financial}]
    },[sectionDataset, getFinancialNameByValue, sectionFinancialInfo])

    const getDatasetIndex = ( datasetValue, columnValue, agg, group ) => dataset.find(data => 
        data.datasetValue === datasetValue 
        && data.columnValue === columnValue 
        && (![agg, group].includes(null) ? agg === data.agg && group === data.group : true)).index //in the old competitive graph dont have agg & group


    //update financial info 
    const updateFinancialInfo = (_, newValue, type) => {
        if (type == "main") setSelectedFinancialInfo(newValue?.value)
        else if (type === "nowcasting") {
            setSectionNowcastingFinancialInfo(newValue?.value)
            setSectionNowcastingDetails((pre)=>{
                return {...pre, nowcasting_financial_info:newValue?.value, nowcasting_financial_info_name:newValue?.name}
            })
        }
    }

    const UpdateNowcastingModelDetails = (_, newValue, type) =>{
        if (type == "period"){
            setSectionNowcastingDetails((pre)=>{
                return {...pre, nowcasting_predict_value:newValue?.value}
            })
        }
        else if (type == "dataset"){
            setSectionNowcastingDetails((pre)=>{
                return {...pre, nowcasting_dataset:newValue?.value}
            })
        }
    }


    // section header
    const handleRemoveSection = () => deleteSection(sectionId)

    const handleDuplicateSection = () => duplicateSection(sectionId)

    const handleUpdateDataset = (index, data) =>setSelectedDataset(updateDataset(index, data))
    
    useEffect(()=>{(ticker && !selectedFinancialInfo) && setSelectedFinancialInfo(suggested);return () => {}},[ticker, selectedFinancialInfo, suggested])

    useEffect(() => {
        sectionId && axios.get(`/api/getSection?sectionId=${sectionId}`)
        .then(res => res.data)
        .then(data => {
            const { metadata:dataset, financial_info:financialInfo, name, is_expand, nowcasting_details: NowcastingDetails, graphs: GraphsData } = data            
            setSectionDataset(dataset)
            setSectionFinancialInfo(financialInfo)
            setSelectedDataset(dataset)
            setSelectedFinancialInfo(financialInfo)
            setSectionName(name)
            setIsExpand(is_expand)
            setSectionNowcastingFinancialInfo(NowcastingDetails?.nowcasting_financial_info||null)
            setSectionNowcastingDetails({...NowcastingDetails,"graph_id":GraphsData?.nowcasting || null})
        })
        return () => {}
    }, [sectionId]) // eslint-disable-line

    useEffect(() => {updateAllSectionsDataset(sectionDataset);return () => {}} ,[sectionDataset]) // eslint-disable-line
    
    // agg
    const setSelectedAggregationBy = (value, index, group=null) => {
        const dataCopy = [...selectedDataset]
        if (!group){
            dataCopy[index]= { ...dataCopy[index], agg:value }
        }
        else{
            dataCopy[index]= { ...dataCopy[index], group:group,agg:value }
        }
        setSelectedDataset(dataCopy)
    }

    const setSelectedGranularity = (value, index, optionName) => {
        const dataCopy = [...selectedDataset]
        const granularity = {...dataCopy[index].granularity, [optionName]: value}
        dataCopy[index]= { ...dataCopy[index], granularity }
        setSelectedDataset(dataCopy)
    }
    
    const setSelectedGroupBy = (currentGroup, index, frequency) => {        
        const dataCopy = [...selectedDataset]
        const currentAgg = dataCopy[index].agg
        dataCopy[index] = {
            ...dataCopy[index],
            group: currentGroup,
            agg: disableAggOptions(currentGroup, currentAgg, frequency)
        }        
        setSelectedDataset(dataCopy)
    }

    // sections
    const updateSectionName = async (sectionName) => {
        const status = await axios({
            method:'post',
            url:'/api/renameSection',
            data:{sectionId:sectionId, sectionName: sectionName}
        })

        status.status === 200 && setSectionName(sectionName)
    }

    const updateAnalyze = () => {
        setSectionDataset(selectedDataset)
        setSectionFinancialInfo(selectedFinancialInfo)
        setTab(0)
        setAnalyze(analyze + 1)
    }

    const changeIsExpand = () => {
        axios({
            method:'post',
            url:'/api/sectionIsExpand',
            data:{sectionId:sectionId, isExpand: !isExpand}
        })
        setIsExpand(!isExpand)
    }

    useEffect(() => {colapseAllSections && setIsExpand(false);return () => {}}, [colapseAllSections])

    // nowcasting
    const getNowCasting = useCallback(async () => {
        const resJson = await axios({
            method: 'POST',
            url: '/api/getNowCastingChangeGraph',
            data: {section_id: sectionId}
        })
        const res = await resJson.data
        let nowcastingData = []
        for ( let property in res.YOY ) {
            const props = getNowcastingProps(property, datasetLabels)
            nowcastingData.push({
                'datasetValue': `${props.datasetName}: ${props.columnName}` , 
                'currentPeriod': res.QOQ[property].value?.value || res.YOY[property].value?.value, 
                'QOQ': res.QOQ[property].change?.value, 
                'YOY': res.YOY[property].change?.value
            })
        }
        return arrangeListByIndex(dataset, nowcastingData)
    }, [datasetLabels, sectionId]) // eslint-disable-line

    // discover
    const updateDatasets = (updatedDatasets) => setSelectedDataset(updatedDatasets)
    
    return (
        <SectionContext.Provider value={{
            sectionId,
            ticker,
            isExpand,
            handleRemoveSection,
            dataset,
            financialInfo: sectionFinancialInfo,
            handleDuplicateSection,
            analyze,
            tab,
            setTab,
            sectionName,
            datasetLabels,
            updateSectionName,
            updateAnalyze,
            changeIsExpand,
            selectedData: {dataset: selectedDataset, financialInfo: selectedFinancialInfo, NowcastingfinancialInfo: sectionNowcastingFinancialInfo},
            setSelectedAggregationBy,
            sectionNowcastingDetails,
            setSectionNowcastingDetails,
            getNowcastingModelInfo,
            UpdateNowcastingModelDetails,
            setSelectedGroupBy,
            updateFinancialInfo,
            handleUpdateDataset,
            updateDatasets,
            isLoading,
            getNowCasting,
            getDatasetIndex,
            dragHandleProps,
            setSelectedGranularity,
            ColumnGranularityOptions,
            setSelectedDataset,
            sectionDataset: {dataset: sectionDataset, financialInfo: sectionFinancialInfo},
        }}>
            { children}
        </SectionContext.Provider>
    )

}

export function useSection() {
    return useContext(SectionContext)
}